/* useBookingFlow — shared booking-wizard state.
   Same data shape every template needs (services list, selected service,
   date/time, customer details, submit handler, navigation). Each template's
   Booking page renders its own UI on top of this hook so the visual treatment
   stays unique while the contract with the backend (POST /api/bookings) stays
   identical and battle-tested. */

import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { tenantHref } from "@/lib/tenant";

export function startingPrice(svc) {
  if (Array.isArray(svc?.tiers) && svc.tiers.length > 0) {
    const prices = svc.tiers.map((t) => Number(t?.price)).filter((n) => Number.isFinite(n) && n > 0);
    if (prices.length) return Math.min(...prices);
  }
  const flat = svc?.price_from ?? svc?.starting_price ?? svc?.price;
  const n = Number(flat);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function formatPrice(n) {
  if (n == null) return null;
  return Number.isInteger(n) ? `$${n.toLocaleString("en-US")}` : `$${n.toFixed(2).replace(/\.00$/, "")}`;
}

export function useBookingFlow({ totalSteps = 4 } = {}) {
  // Start empty — never seed with hardcoded placeholder names like
  // "Signature Sitting / Editorial Sitting / Brand Story Half-Day /
  // Milestone Edition". Those used to leak into every tenant whose
  // /api/services either returned [] (new studio, all services deleted)
  // or hadn't resolved yet (slow network, paint before fetch), making
  // Skenzy's booking page look like it belonged to Allure's Lumetier demo.
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [selectedId, setSelectedId] = useState("");
  const [tierName, setTierName] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [details, setDetails] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  // Terms acceptance — customers now MUST tick the checkbox on the review
  // step before we send `policy_accepted: true` to the backend. Prior to
  // this, the payload hardcoded `policy_accepted: true` so the backend's
  // validation was theatre — the customer never saw or acknowledged the
  // studio's booking policy. This is the enforcement path.
  const [termsAccepted, setTermsAccepted] = useState(false);
  // `policyText` is the studio's `site_settings.booking_policy` (free-text).
  // When empty, the BookingTermsAcceptance component falls back to a
  // sensible generic policy so a fresh tenant still has SOMETHING for the
  // customer to accept.
  const [policyText, setPolicyText] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/services")
      .then((r) => { if (Array.isArray(r.data)) setServices(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Fetch site settings in parallel — we only need `booking_policy` from
    // it. Failure is silent: the review checkbox falls back to the generic
    // policy copy provided by BookingTermsAcceptance.
    api.get("/site")
      .then((r) => { if (r?.data?.booking_policy) setPolicyText(r.data.booking_policy); })
      .catch(() => {});
    const preselected = searchParams.get("service");
    if (preselected) setSelectedId(preselected);
  }, [searchParams]);

  const selected = useMemo(
    () => services.find((s) => (s.id === selectedId) || (s.slug === selectedId)) || services[0],
    [services, selectedId]
  );

  // Auto-default tier to the first one when the service changes.
  useEffect(() => {
    if (!selected) return;
    const firstTier = selected.tiers?.[0]?.name;
    if (firstTier && !selected.tiers.some((t) => t.name === tierName)) {
      setTierName(firstTier);
    }
  }, [selected, tierName]);

  const selectedTier = useMemo(
    () => selected?.tiers?.find((t) => t.name === tierName) || selected?.tiers?.[0] || null,
    [selected, tierName]
  );

  const canForward = useMemo(() => {
    // Compute per-step field validity FIRST, then AND with termsAccepted
    // on the final step. Otherwise a template with totalSteps === 3
    // (editorial/bold) would short-circuit on the step-3 check and never
    // reach the terms guard, letting customers submit without ticking the
    // box. The lint-y way of writing this is verbose but correct.
    let fieldsOk;
    if (step === 1) fieldsOk = Boolean(selected);
    else if (step === 2) fieldsOk = Boolean(date);
    else if (step === 3) fieldsOk = Boolean(details.name && details.email);
    else fieldsOk = true;

    if (step === totalSteps) return fieldsOk && termsAccepted;
    return fieldsOk;
  }, [step, totalSteps, selected, date, details, termsAccepted]);

  const submit = useCallback(async () => {
    // Defensive — the UI already blocks this via `canForward`, but a rogue
    // caller invoking submit() directly should still be rejected here.
    if (!termsAccepted) {
      toast.error("Please accept the booking terms before continuing.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        service_id: selected.id,
        tier_name:
          tierName
          || (selected.tiers && selected.tiers[0] && selected.tiers[0].name)
          || selected.name,
        session_date: date,
        session_time: time,
        customer_name: details.name,
        customer_email: details.email,
        customer_phone: details.phone,
        notes: details.notes,
        policy_accepted: termsAccepted,
      };
      const { data } = await api.post("/bookings", payload);
      toast.success("Booking request sent.");
      const isAdmin = searchParams.get("admin") === "1";
      if (isAdmin) {
        navigate("/admin/bookings");
        return;
      }
      if (data && data.reference) {
        // Use tenantHref so /s/{slug}/booking/confirmation/{ref} resolves on
        // tenant routes and /booking/confirmation/{ref} on the founder brand.
        // Pass the full booking record + the resolved service so the
        // confirmation page can render session details + policy + the
        // service's own hero image without an extra API call (the
        // `GET /bookings/{ref}` endpoint was retired in SEC-003).
        navigate(tenantHref(`/booking/confirmation/${data.reference}`), {
          state: {
            booking: data,
            serviceImage: selected?.image_url || null,
          },
        });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't send your booking — please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [selected, tierName, date, time, details, termsAccepted, navigate, searchParams]);

  const goBack = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);
  const goForward = useCallback(() => {
    setStep((s) => Math.min(totalSteps, s + 1));
  }, [totalSteps]);

  return {
    services,
    loading,
    step, setStep, goBack, goForward, totalSteps,
    selected, selectedId, setSelectedId,
    tierName, setTierName, selectedTier,
    date, setDate, time, setTime,
    details, setDetails,
    submitting, submit,
    canForward,
    termsAccepted, setTermsAccepted,
    policyText,
  };
}
